import { Injectable, Inject } from '@angular/core';
import { Language } from '../models/translation/language';
import { BehaviorSubject } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { Locale } from '../models/translation/locale';
import { locale as enLang } from '../config/i18n/en';
import { locale as faLang } from '../config/i18n/fa';
import { locale as ruLang } from '../config/i18n/ru';
import { locale as arLang } from '../config/i18n/ar';

@Injectable({
    providedIn: 'root'
})
export class TranslationService {
    languages: Language[];
    private langIds: string[] = [];
    private currentLang = new BehaviorSubject<string>('en');
    currentLang$ = this.currentLang.asObservable();
    localStorage: any;

    constructor(
        private translate: TranslateService,
    ) {
        this.initLanguages();
        this.getLangFromUrl();
        this.translate.setDefaultLang('en');
        this.setConfigOnDesign();
        this.loadTranslations(enLang, faLang, arLang, ruLang);
    }

    private setConfigOnDesign(): void {
        const currentLang = this.getCurrentLang();
        if (currentLang) {
            this.translate.use(currentLang.uniqueSeoCode);
            this.changeDirection(currentLang.rtl);
            this.currentLang.next(currentLang.uniqueSeoCode);
        } else {
            this.translate.use('en');
            this.changeDirection(false);
            localStorage.setItem('language', 'en');
        }
        document.getElementsByTagName('html')[0].setAttribute('lang', currentLang.uniqueSeoCode);
        document.getElementsByTagName('body')[0].setAttribute('lang', currentLang.uniqueSeoCode);
    }

    initLanguages(): void {
        this.languages = [
            { id: 1, languageCulture: 'en-US', name: 'English', rtl: false, flagImageFileName: 'us.svg', uniqueSeoCode: 'en', lang: 'en' },
            { id: 2, languageCulture: 'fa-IR', name: 'فارسی', rtl: true, flagImageFileName: 'fa.svg', uniqueSeoCode: 'fa', lang: 'fa' },
            { id: 5, languageCulture: 'ar-AE', name: 'Arabic', rtl: true, flagImageFileName: 'ae.png', uniqueSeoCode: 'ar', lang: 'ar' },
            // {id: 3,languageCulture: 'ru-RU',name: 'Russia', rtl: false,flagImageFileName: 'ru.svg',uniqueSeoCode: 'ru', lang: 'ru'}
        ];
    }

    loadTranslations(...args: Locale[]): void {
        const locales = [...args];

        locales.forEach(locale => {
            // use setTranslation() with the third argument set to true
            // to append translations instead of replacing them
            this.translate.setTranslation(locale.lang, locale.data, true);

            this.langIds.push(locale.lang);
        });

        // add new languages to the list
        this.translate.addLangs(this.langIds);
    }
    setLanguage(lang: Language): void {
        if (lang &&
            lang.uniqueSeoCode !== this.getCurrentLang().uniqueSeoCode
        ) {
            localStorage.setItem('language', lang.uniqueSeoCode);
            location.reload();

        }
    }

    changeDirection(rtl: boolean): void {
        const cssRtl: any = document.getElementById('rtlStyle');
        if (rtl) {
            cssRtl.disabled = false;
            document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
            document.getElementsByTagName('body')[0].setAttribute('dir', 'rtl');
        } else {
            cssRtl.disabled = true;
            document.getElementsByTagName('html')[0].setAttribute('dir', 'ltr');
            document.getElementsByTagName('body')[0].setAttribute('dir', 'ltr');
        }
    }

    getCurrentLang(): Language {
        const currentLang = localStorage.getItem('language');

        if (currentLang && this.languages.some(item => item.uniqueSeoCode === currentLang)) {
            return this.languages.find(item => item.uniqueSeoCode === currentLang);
        }

        // If the language is not found or currentLang is null, return the default language "en"
        localStorage.setItem('language', 'en');
        return { id: 1, languageCulture: 'en-US', name: 'English', rtl: false, flagImageFileName: 'us.svg', uniqueSeoCode: 'en', lang: 'en' };
    }

    setServerLanguages(languages: Language[]): void {
        this.languages = languages;
        // Loop through each language and set the value of lang equal to uniqueSeoCode
        this.languages.forEach(language => {
            language.lang = language.uniqueSeoCode;
        });
        this.setConfigOnDesign();
    }

    getLangFromUrl(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const lang = urlParams.get('lang');
        if (lang) {
            for (const item of this.languages) {
                if (item.uniqueSeoCode === lang) {
                    localStorage.setItem('language', item.uniqueSeoCode);
                    this.translate.use(item.uniqueSeoCode);
                    this.changeDirection(item.rtl);
                    this.currentLang.next(item.uniqueSeoCode);
                }
            }
            setTimeout(() => {
                let url = window.location.toString();
                let urlWithoutLang = url.substring(0, (url.indexOf('lang=') - 1));
                window.history.replaceState({}, document.title, urlWithoutLang);
            }, 300)
        }

    }
}